import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  CommunityAdminHostReviewFlag,
  CommunityAdminProjectReview,
  CommunityAdminReviewSignalFlag,
  CommunityAdminProject,
} from "@squeek/module-contracts/community-admin-v1";
import type { ChipTone } from "@squeek/ui";
import { useEffect, useRef, useState, type ReactNode } from "react";
import type { ProjectScreenApi } from "./tabs/projects";
import { PROJECT_COMMENT_MAX, type CommunityReviewPanelTally } from "@squeek/module-contracts/community-review-v1";
import { errorMessage } from "./api";
import { ProjectStatusChip, QueryState } from "./review-components";
import { ProjectCover } from "./cover";
import { ageInDays, compactNumber, formatAge, formatDate, formatPercent, shortId } from "./format";
import { PersonAvatar, personLabel, RiskBadge, StandingLine, TierMark } from "./person";
import { assessRisk, NEW_X_DAYS, FEW_FOLLOWERS } from "./risk";

const HOST_FLAG_LABELS: Record<CommunityAdminHostReviewFlag, string> = {
  brand_lookalike: "Host looks like Squeekchain",
  idn: "International (xn--) host",
};

const SIGNAL_FLAG_LABELS: Record<CommunityAdminReviewSignalFlag, { label: string; tone: ChipTone }> = {
  domain_new: { label: "Domain registered < 30 days", tone: "stalled" },
  domain_expired: { label: "Domain expired", tone: "failing" },
  domain_reregistered: { label: "Domain re-registered", tone: "failing" },
  site_failing: { label: "Site check failing", tone: "failing" },
  site_parked: { label: "Parking page", tone: "failing" },
  marker_missing: { label: "No project marker", tone: "stalled" },
  content_changed: { label: "Content changed since approval", tone: "failing" },
  repo_missing: { label: "No source link", tone: "stalled" },
  repo_not_repository: { label: "Source is not a repository", tone: "failing" },
  repo_not_found: { label: "Repository not found", tone: "failing" },
  repo_new: { label: "Repository < 30 days", tone: "stalled" },
  repo_owner_new: { label: "GitHub account < 90 days", tone: "stalled" },
  repo_few_commits: { label: "≤ 3 commits", tone: "stalled" },
  repo_fork: { label: "Forked repository", tone: "stalled" },
  repo_proof_missing: { label: "Project id not in repository", tone: "failing" },
  repo_no_reference: { label: "No Squeekchain reference in repository", tone: "stalled" },
  repo_model_concern: { label: "AI: repository may not be this project", tone: "failing" },
  imd_default_name: { label: "Default project name", tone: "stalled" },
  imd_prompt_copied: { label: "Objective copies the prompt", tone: "stalled" },
  imd_duplicate_objective: { label: "Duplicate objective", tone: "stalled" },
  imd_unusual_template: { label: "Unusual template", tone: "stalled" },
  imd_review_failed: { label: "IMD review failed", tone: "failing" },
  imd_no_repo: { label: "No delivered repository", tone: "stalled" },
  imd_scan_incomplete: { label: "Source scan incomplete", tone: "stalled" },
  imd_no_reference: { label: "No Squeekchain reference found", tone: "stalled" },
  imd_model_unavailable: { label: "AI review unavailable", tone: "stalled" },
  imd_model_concern: { label: "AI review concern", tone: "stalled" },
};

export const signalLabel = (flag: CommunityAdminReviewSignalFlag) => SIGNAL_FLAG_LABELS[flag].label;
export const DECLINE_REASONS = ["No Squeekchain integration", "Doesn't work / not functional", "Duplicate or low-effort submission", "No code delivered", "Repository isn't this project"];
export function DeclineReason({ value, onChange }: { value: string; onChange: (value: string) => void }) {
  const input = useRef<HTMLTextAreaElement>(null);
  useEffect(() => { input.current?.focus(); }, []);
  return <div className="pr-decline">
    <label className="pr-field"><span>Preset reason</span><select className="c-input" aria-label="Preset decline reason" value="" onChange={(event) => onChange(event.target.value)}><option value="">Choose a reason…</option>{DECLINE_REASONS.map((reason) => <option key={reason}>{reason}</option>)}</select></label>
    <label className="pr-field"><span>Reason shown to the owner</span><textarea className="c-input" ref={input} aria-label="Decline reason" value={value} minLength={3} maxLength={500} rows={3} onChange={(event) => onChange(event.target.value)} /></label>
    <small className="c-fg-3">{value.trim().length}/500 characters, at least 3. The owner can fix the project and resubmit.</small>
  </div>;
}

export function ProjectDetailPane({ project, busy, onDecision, onOwner, api, mode = "admin", override = false, panel }: {
  project: CommunityAdminProject; busy: boolean; onDecision: (action: "approve" | "decline", reason?: string, immediate?: boolean) => Promise<{ ok: boolean; message: string }>; onOwner: () => void;
  api: Pick<ProjectScreenApi, "projectReview" | "projectPanel" | "projectComments" | "addProjectComment" | "deleteProjectComment">; mode?: "admin" | "panel"; override?: boolean; panel?: CommunityReviewPanelTally | undefined;
}) {
  const [declining, setDeclining] = useState(false);
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const [noteVisible, setNoteVisible] = useState(false);
  const [immediate, setImmediate] = useState(false);
  const [outcome, setOutcome] = useState<{ ok: boolean; message: string } | null>(null);
  // Run a decision and report it in the card: success closes the decline form so it can't be re-sent.
  const submit = (action: "approve" | "decline", text?: string) => {
    setOutcome(null);
    void onDecision(action, text, immediate).then((result) => {
      setOutcome(result);
      if (result.ok) { setDeclining(false); setReason(""); setNote(""); }
    });
  };
  const review = useQuery({ queryKey: ["project-review", project.id], queryFn: () => api.projectReview(project.id) });
  const adminPanel = useQuery({ queryKey: ["project-panel", project.id, project.updatedAt], queryFn: () => api.projectPanel!(project.id), enabled: mode === "admin" && !!api.projectPanel });
  const tally = mode === "panel" ? panel : adminPanel.data;
  // Panel votes close after a decision; admins may still withdraw an approval.
  const declinable = !project.removal && (mode === "admin" || immediate || (!project.approval && !project.decline));
  const approvable = declinable && !project.approval && (project.status === "verified" || project.origin === "imd");
  const owner = project.owner;
  const risk = assessRisk(owner);
  const score = review.data?.reviewScore ?? project.reviewScore;
  const verdict = review.data?.modelReview?.verdict ?? project.review?.verdict;
  const verdictSummary = verdict
    ? `${verdict.usesSqueekchain ? "Uses Squeekchain" : "No Squeekchain integration"} · ${verdict.appearsFunctional ? "Appears functional" : "May not work"}${verdict.matchesProject === undefined ? "" : verdict.matchesProject ? " · Code matches project" : " · Code may not be this project"} · effort ${verdict.effort}/5`
    : `AI opinion: ${review.data?.modelReview?.status ?? "not checked yet"}`;
  const ownerName = personLabel({ personId: owner.personId, x: owner.xUsername ? { name: owner.xName ?? "", username: owner.xUsername } : null });
  const httpsUrl = (url: string | null) => url !== null && URL.canParse(url) && new URL(url).protocol === "https:" ? url : null;
  const site = httpsUrl(project.demoUrl);
  const source = httpsUrl(project.repoUrl);
  const imdJob = project.imdJobId ? `https://explorer.imd.fun/jobs/${encodeURIComponent(project.imdJobId)}` : null;
  const declineValid = reason.trim().length >= 3 && reason.trim().length <= 500;
  return <article className="pr">
    {/* One card holds everything needed to decide; the sections below it are supplemental. */}
    <section className="pr-main" aria-labelledby={`project-title-${project.id}`}>
      <header className="pr-main__head">
        <div className="pr-main__cover"><ProjectCover cover={project.cover} width={136} /></div>
        <div className="pr-main__headline">
          <div className="pr-main__title">
            <h2 id={`project-title-${project.id}`} title={project.name}>{project.name}</h2>
            <ProjectStatusChip {...project} />
          </div>
          <p className="pr-main__meta">{project.origin === "imd" ? "IMD" : project.origin} · submitted {formatDate(project.createdAt)} · updated {formatDate(project.updatedAt)}</p>
          <p className="pr-main__verdict" title={verdictSummary}>{verdictSummary}</p>
        </div>
        <span className="pr-score" data-band={score == null ? "none" : score >= 60 ? "high" : score < 20 ? "low" : "mid"} aria-label={`Score ${score ?? "unknown"}`}><small>Score</small>{score ?? "—"}</span>
        {project.decline ? <p className="pr-declined">Declined: {project.decline.reason}</p> : null}
      </header>

      <div className="pr-main__synopsis">
        <h3>Synopsis</h3>
        <p>{project.integration}</p>
      </div>

      <div className="pr-main__owner" aria-label="Submitted by">
        <PersonAvatar personId={owner.personId} profileImageUrl={owner.profileImageUrl} size={44} />
        <div className="pr-main__owner-name">
          <span className="c-fg-3">Submitted by</span>
          <strong className="c-truncate">{ownerName}</strong>
          <span className="c-fg-3 c-truncate">{owner.xUsername ? `@${owner.xUsername}` : `Invite code ${owner.referralCode}`}</span>
        </div>
        <dl className="pr-main__owner-facts">
          <div><dt>Standing</dt><dd className="pr-inline"><TierMark standing={owner.standing} /><StandingLine standing={owner.standing} /></dd></div>
          <div><dt>X</dt><dd>{owner.xUsername ? <>{formatAge(owner.xCreatedAt)} old · {compactNumber(owner.xFollowers)} followers</> : <span className="c-fg-3">Not linked</span>}</dd></div>
          <div><dt>Bot risk</dt><dd className="pr-inline"><RiskBadge risk={risk} />{risk.reasons.length ? <span className="c-fg-2">{risk.reasons.filter((item) => item !== "Disqualified by moderation").join(" · ")}</span> : null}</dd></div>
        </dl>
        {mode === "admin" ? <button type="button" className="c-btn" data-size="small" onClick={onOwner}>Open person</button> : null}
      </div>

      <nav className="pr-main__links" aria-label="Project links">
        {site ? <a className="pr-link" data-primary="true" href={site} target="_blank" rel="noopener noreferrer nofollow" referrerPolicy="no-referrer" title={site}><span>Open project site ↗</span><small className="c-truncate">{site.replace(/^https:\/\//, "")}</small></a> : <span className="pr-link" data-disabled="true"><span>No project site</span><small>{project.demoUrl ?? "Not provided"}</small></span>}
        {source ? <a className="pr-link" href={source} target="_blank" rel="noopener noreferrer nofollow" referrerPolicy="no-referrer" title={source}><span>View source ↗</span><small className="c-truncate">{source.replace(/^https:\/\//, "")}</small></a> : <span className="pr-link" data-disabled="true"><span>No source</span><small>{project.repoUrl ?? "Not provided"}</small></span>}
        {imdJob ? <a className="pr-link" href={imdJob} target="_blank" rel="noopener noreferrer"><span>IMD job ↗</span><small>explorer.imd.fun</small></a> : null}
      </nav>

      {tally ? <p className="pr-main__meta" role="status">Approve {tally.approve} · Decline {tally.decline} · decides at {tally.quorum} votes{mode === "panel" ? ` · Your vote: ${tally.myVote ?? "not voted"}` : ""}</p> : null}
      {outcome ? <p className="pr-outcome" data-ok={outcome.ok} role={outcome.ok ? "status" : "alert"}>{outcome.message}</p> : null}
      {adminPanel.error ? <p className="c-alert" role="alert">Panel tally unavailable.</p> : null}
      {mode === "panel" && override && !project.removal ? <label className="pr-field"><span><input type="checkbox" checked={immediate} disabled={busy} onChange={(event) => { setImmediate(event.target.checked); setDeclining(false); }} /> Decide now</span><small>Admin override: decide without waiting for panel votes.</small></label> : null}
      {declining ? <form className="pr-decline-form" onSubmit={(event) => { event.preventDefault(); if (declineValid && !busy) submit("decline", reason.trim()); }}>
        <DeclineReason value={reason} onChange={setReason} />
      </form> : null}
      {mode === "admin" && approvable && noteVisible && !declining ? <input className="c-input pr-note" aria-label="Optional approval note" placeholder="Optional private approval note (3–500 characters)" maxLength={500} value={note} onChange={(event) => setNote(event.target.value)} /> : null}

      <footer className="pr-cta">
        {declining ? <>
          <button type="button" className="c-btn pr-cta__btn" onClick={() => setDeclining(false)}>Cancel</button>
          <button type="button" className="c-btn pr-cta__btn" data-variant="danger" disabled={busy || !declineValid} onClick={() => submit("decline", reason.trim())}>{busy ? "Saving…" : mode === "panel" && !immediate ? "Confirm decline vote" : "Confirm decline"}</button>
        </> : <>
          {approvable ? <button id="project-approve" type="button" className="c-btn pr-cta__btn" data-variant="approve" onFocus={() => setNoteVisible(true)} disabled={busy || (note.trim().length > 0 && note.trim().length < 3)} onClick={() => { setNoteVisible(true); submit("approve", note.trim() || undefined); }}>{mode === "panel" && !immediate ? tally?.myVote === "approve" ? "Voted approve ✓" : "Vote approve" : "Approve"} <span className="c-kbd">A</span></button> : null}
          {declinable ? <button id="project-decline" type="button" className="c-btn pr-cta__btn" data-variant="danger" aria-expanded={false} disabled={busy} onClick={() => { setOutcome(null); setDeclining(true); }}>{mode === "panel" && !immediate ? tally?.myVote === "decline" ? "Voted decline ✓" : "Vote decline" : project.approval ? "Decline and hide" : "Decline"} <span className="c-kbd">D</span></button> : null}
          {!declinable ? <p className="c-fg-3 pr-cta__none">{project.removal ? "Removed" : "Decided"} — no decision pending.</p> : null}
        </>}
      </footer>
    </section>

    <ProjectNotes api={api} projectId={project.id} />

    <div className="pr-body">
      <h3 className="pr-supplemental">Supplemental evidence</h3>
      <div className="pr-evidence">
        <QueryState query={review}>{(data) => <ProjectReviewDetails review={data} mode={mode} />}</QueryState>
      </div>
    </div>
  </article>;
}

/** Internal notes from the admin and the review panel. Members never see them. */
function ProjectNotes({ api, projectId }: { api: Pick<ProjectScreenApi, "projectComments" | "addProjectComment" | "deleteProjectComment">; projectId: string }) {
  const client = useQueryClient();
  const key = ["project-comments", projectId];
  const [draft, setDraft] = useState("");
  const comments = useQuery({ queryKey: key, queryFn: () => api.projectComments(projectId) });
  const add = useMutation({
    mutationFn: () => api.addProjectComment(projectId, draft.trim()),
    onSuccess: (data) => { client.setQueryData(key, data); setDraft(""); },
  });
  const remove = useMutation({
    mutationFn: (commentId: string) => api.deleteProjectComment(projectId, commentId),
    onSuccess: (data) => client.setQueryData(key, data),
  });
  const list = comments.data?.comments ?? [];
  const length = draft.trim().length;
  return <section className="pr-card pr-notes" aria-labelledby={`notes-${projectId}`}>
    <h3 id={`notes-${projectId}`}>Team notes <span className="c-fg-3 c-num">{list.length || ""}</span></h3>
    <p className="c-fg-3">Internal feedback for the admin and review panel. The project owner never sees these.</p>
    {comments.isPending ? <p className="c-fg-3">Loading notes…</p> : comments.error ? <p className="pr-warn-text" role="alert">Notes failed to load: {errorMessage(comments.error)}</p> : list.length === 0 ? <p className="c-fg-3">No notes yet.</p> : (
      <ol className="pr-notes__list">
        {list.map((comment) => {
          const name = personLabel({ personId: comment.author.personId, x: comment.author.xUsername ? { name: comment.author.xName ?? "", username: comment.author.xUsername } : null });
          return <li key={comment.id}>
            <PersonAvatar personId={comment.author.personId} profileImageUrl={comment.author.profileImageUrl} size={28} />
            <div className="pr-notes__body">
              <p className="pr-notes__meta">
                <strong>{name}</strong>
                {comment.author.role === "admin" ? <span className="c-badge" data-tone="info">Admin</span> : null}
                <span className="c-fg-3" title={formatDate(comment.createdAt)}>{relativeTime(comment.createdAt)}</span>
                {comment.deletable ? <button type="button" className="c-icon-btn" disabled={remove.isPending} onClick={() => { if (window.confirm("Delete this note?")) remove.mutate(comment.id); }}>Delete</button> : null}
              </p>
              <p className="pr-notes__text">{comment.body}</p>
            </div>
          </li>;
        })}
      </ol>
    )}
    <form className="pr-notes__form" onSubmit={(event) => { event.preventDefault(); if (length > 0 && length <= PROJECT_COMMENT_MAX && !add.isPending) add.mutate(); }}>
      <textarea className="c-input" aria-label="Add a team note" placeholder="Add a note for the team…" rows={3} maxLength={PROJECT_COMMENT_MAX} value={draft}
        onChange={(event) => setDraft(event.target.value)}
        onKeyDown={(event) => { if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) event.currentTarget.form?.requestSubmit(); }} />
      <div className="pr-notes__actions">
        <span className="c-fg-3 c-num">{length}/{PROJECT_COMMENT_MAX} · ⌘↵ to post</span>
        <button type="submit" className="c-btn" data-variant="primary" disabled={length === 0 || length > PROJECT_COMMENT_MAX || add.isPending}>{add.isPending ? "Posting…" : "Post note"}</button>
      </div>
      {add.error || remove.error ? <p className="pr-warn-text" role="alert">{errorMessage(add.error ?? remove.error)}</p> : null}
    </form>
  </section>;
}

function relativeTime(iso: string, now = Date.now()): string {
  const minutes = Math.round((now - Date.parse(iso)) / 60_000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} h ago`;
  return formatDate(iso);
}

export interface ReviewWarning {
  readonly key: string;
  readonly label: string;
  readonly tone: ChipTone;
}

/** Every warning chip of a review, most serious signals first within each source. */
export function reviewWarnings(review: CommunityAdminProjectReview, now = Date.now()): ReviewWarning[] {
  const warnings: ReviewWarning[] = [
    ...review.hostFlags.map((flag) => ({ key: flag, label: HOST_FLAG_LABELS[flag], tone: "failing" as const })),
    ...review.flags.map((flag) => ({ key: flag, ...SIGNAL_FLAG_LABELS[flag] })),
  ];
  const { member } = review;
  if (member.disqualified) warnings.push({ key: "member_disqualified", label: "Member disqualified", tone: "failing" });
  const xAge = ageInDays(member.xCreatedAt, now);
  if (xAge !== null && xAge < NEW_X_DAYS) warnings.push({ key: "x_new", label: `X account ${xAge} days old`, tone: "stalled" });
  if (member.xFollowers !== null && member.xFollowers < FEW_FOLLOWERS) {
    warnings.push({ key: "x_followers", label: `${member.xFollowers} X followers`, tone: "stalled" });
  }
  if (member.cluster !== null) {
    warnings.push({ key: "cluster", label: `Funding cluster of ${member.cluster.size}`, tone: "failing" });
  }
  const buildingInviters = member.inviters.filter((inviter) => inviter.projectCount > 0).length;
  if (buildingInviters > 0) {
    warnings.push({ key: "inviters", label: `${buildingInviters} inviter${buildingInviters === 1 ? "" : "s"} with projects`, tone: "stalled" });
  }
  if (review.siblings.length > 0) {
    warnings.push({ key: "siblings", label: `${review.siblings.length} sibling domain${review.siblings.length === 1 ? "" : "s"}`, tone: "failing" });
  }
  if (review.similar.length > 0) {
    warnings.push({ key: "similar", label: `${review.similar.length} similar description${review.similar.length === 1 ? "" : "s"}`, tone: "failing" });
  }
  return warnings.sort((a, b) => Number(b.tone === "failing") - Number(a.tone === "failing"));
}

function Facts({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="pr-facts">
      <h4>{title}</h4>
      <dl className="c-kv">{children}</dl>
    </section>
  );
}

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <>
      <dt>{label}</dt>
      <dd>{children ?? <span className="c-fg-3">—</span>}</dd>
    </>
  );
}

const handle = (person: { xUsername: string | null; personId: string; referralCode: string }) =>
  `${person.xUsername === null ? shortId(person.personId) : `@${person.xUsername}`} (${person.referralCode})`;

/** Score reasoning, warnings and usage first; technical evidence collapsed below. */
export function ProjectReviewDetails({ review, now = Date.now(), mode = "admin" }: { review: CommunityAdminProjectReview; now?: number; mode?: "admin" | "panel" }) {
  const warnings = reviewWarnings(review, now);
  const { domain, site, repo } = review;
  return (
    <div className="pr-stack">
      <section className="pr-card">
        <h3>Why this score</h3>
        {review.scoreBreakdown?.length ? <ul className="pr-score-parts">{review.scoreBreakdown.map((part) => <li key={part.key}><span>{part.label}</span><strong data-sign={part.points >= 0 ? "plus" : "minus"}>{part.points >= 0 ? "+" : ""}{part.points}</strong></li>)}
          <li className="pr-score-total"><span>Total before clamping</span><strong>{review.scoreBreakdown.reduce((sum, part) => sum + part.points, 0)}</strong></li></ul> : null}
        {review.modelReview?.verdict ? <blockquote className="pr-verdict"><span className="c-fg-3">AI reviewer</span>{review.modelReview.verdict.reason}</blockquote> : <p className="c-fg-3">AI opinion: {review.modelReview?.status ?? "not checked yet"}</p>}
        <p className="c-fg-3">Team approval required; ranking does not verify a project.</p>
      </section>
      <section className="pr-card">
        <h3>Warnings <span className="c-fg-3 c-num">{warnings.length || ""}</span></h3>
        {warnings.length ? <ul className="pr-warnings">{warnings.map((warning) => <li key={warning.key} data-tone={warning.tone}><span>{warning.label}</span><small>{warning.tone === "failing" ? "Inspect before approving" : "Advisory"}</small></li>)}</ul> : <p className="c-fg-3">No warnings.</p>}
      </section>
      <section className="pr-card"><h3>Squeekchain usage</h3>{review.imdSignals?.usage && typeof review.imdSignals.usage === "object" ? <UsageEvidence usage={review.imdSignals.usage as Record<string, unknown>} /> : review.sourceScan ? <UsageEvidence usage={review.sourceScan} /> : <p className="c-fg-3">No source scan yet.</p>}</section>
      <details className="pr-card pr-details"><summary>Member, domain, site and repository facts</summary>
      <div className="pr-details__body">
        <Facts title="Member evidence">
          <Fact label="Member">{handle(review.member)}</Fact>
          {mode === "admin" ? <Fact label="Wallets">{review.member.walletCount}</Fact> : null}
          <Fact label="Invited by">{review.member.inviters.map((inviter) => `${handle(inviter)} · ${inviter.projectCount} project(s)`).join(" ← ") || "none"}</Fact>
          <Fact label="Funding cluster">{review.member.cluster ? `${review.member.cluster.id} · ${review.member.cluster.size} people · ${review.member.cluster.reason}` : "none"}</Fact>
        </Facts>
        <Facts title="Domain (RDAP)">
          {domain === null ? <Fact label="Status">not checked yet</Fact> : (
            <>
              <Fact label="Registered domain">{domain.registeredDomain}</Fact>
              <Fact label="Registered">{domain.registeredAt === null ? null : `${formatDate(domain.registeredAt)} (${domain.ageDays} days)`}</Fact>
              <Fact label="Expires">{domain.expiresAt === null ? null : formatDate(domain.expiresAt)}</Fact>
              <Fact label="Registrar">{domain.registrar}</Fact>
              <Fact label="Checked">{formatDate(domain.checkedAt)}</Fact>
            </>
          )}
        </Facts>
        <Facts title="Site check">
          {site === null ? <Fact label="Status">not checked yet</Fact> : (
            <>
              <Fact label="Result">{site.result === "ok" ? `HTTP ${site.status}` : site.result}</Fact>
              <Fact label="Final URL">{site.finalUrl}</Fact>
              <Fact label="Title">{site.title}</Fact>
              <Fact label="Marker">{site.markerFound === null ? null : site.markerFound ? "present" : "missing"}</Fact>
              <Fact label="Parked">{site.parked === null ? null : site.parked ? "yes" : "no"}</Fact>
              <Fact label="Failures in a row">{site.consecutiveFailures}</Fact>
              <Fact label="Vs. approval">
                {site.baselineApprovedAt === null ? "no baseline" : `${formatPercent(site.similarity)} similar`}
              </Fact>
              <Fact label="Checked">{formatDate(site.checkedAt)}</Fact>
            </>
          )}
        </Facts>
        <Facts title="Source">
          {repo === null ? <Fact label="Status">not checked yet</Fact> : (
            <>
              <Fact label="Repository">{repo.owner === null ? repo.url : `${repo.host}/${repo.owner}/${repo.name ?? "?"}`}</Fact>
              {repo.error === null ? null : <Fact label="GitHub">{repo.error}</Fact>}
              <Fact label="Created">{repo.createdAt === null ? null : formatDate(repo.createdAt)}</Fact>
              <Fact label="Last push">{repo.pushedAt === null ? null : formatDate(repo.pushedAt)}</Fact>
              <Fact label="Commits">{repo.commitCount === null ? null : `${repo.commitCount}${repo.commitCountCapped ? "+" : ""}`}</Fact>
              <Fact label="Size">{repo.sizeKb === null ? null : `${repo.sizeKb} KB`}</Fact>
              <Fact label="Owner since">{repo.ownerCreatedAt === null ? null : formatDate(repo.ownerCreatedAt)}</Fact>
              <Fact label="Owner repos">{repo.ownerPublicRepos}</Fact>
              <Fact label="Project id">{repo.proof === null ? null : repo.proof === "found" ? `found${repo.proofSeenAt === null ? "" : ` (since ${formatDate(repo.proofSeenAt)})`}` : "missing"}</Fact>
            </>
          )}
        </Facts>
      </div>
      </details>
      <details className="pr-card pr-details"><summary>Similar and sibling projects, site excerpt</summary><div className="pr-details__body">
      {site?.textExcerpt ? (
        <Facts title="Visible text (excerpt)">
          <Fact label="Text"><span className="whitespace-pre-wrap font-sans">{site.textExcerpt}</span></Fact>
        </Facts>
      ) : null}
      {review.siblings.length > 0 ? (
        <Facts title="Sibling domains (other members)">
          {review.siblings.map((sibling) => (
            <Fact key={sibling.projectId} label={sibling.match === "registered_within_3h" ? "within 3 h" : "same registrar, day"}>
              {sibling.name} · {sibling.domain ?? "—"} · {handle(sibling.owner)} · {formatDate(sibling.registeredAt)} · {sibling.registrar ?? "—"}
            </Fact>
          ))}
        </Facts>
      ) : null}
      {review.similar.length > 0 ? (
        <Facts title="Similar descriptions">
          {review.similar.map((other) => (
            <Fact key={other.projectId} label={formatPercent(other.similarity)}>{other.name} · {handle(other.owner)}</Fact>
          ))}
        </Facts>
      ) : null}
      </div></details>
      <details className="pr-card pr-details"><summary>Raw signals JSON</summary><pre className="pr-json">{JSON.stringify({ imdSignals: review.imdSignals, sourceScan: review.sourceScan, modelReview: review.modelReview, reviewedAt: review.imdReviewedAt ?? review.sourceReviewedAt, reviewKey: review.imdReviewKey }, null, 2)}</pre></details>
    </div>
  );
}

function UsageEvidence({ usage }: { usage: Record<string, unknown> }) {
  const files = Array.isArray(usage.matchingFiles) ? usage.matchingFiles.map(String) : null;
  return <div className="pr-usage">
    <p><strong className="c-num">{typeof usage.references === "number" ? usage.references : "Unknown"}</strong> references · <span className={usage.complete === true ? "c-fg-2" : "pr-warn-text"}>{usage.complete === true ? "complete scan" : "incomplete scan"}</span></p>
    {usage.error ? <p className="pr-warn-text">{String(usage.error)}</p> : null}
    {files === null ? <p className="c-fg-3">Matching files: {String(usage.matchingFiles ?? "unknown")}</p> : files.length ? <ul className="pr-files">{files.map((file) => <li key={file} className="c-mono">{file}</li>)}</ul> : <p className="c-fg-3">No matching files.</p>}
    {Array.isArray(usage.excerpts) && usage.excerpts.length ? <details><summary>Source excerpts ({usage.excerpts.length})</summary><pre className="pr-json">{JSON.stringify(usage.excerpts, null, 2)}</pre></details> : null}
  </div>;
}
